import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, IconButton } from '@mui/material';
import { GetApp as InstallIcon, Close as CloseIcon } from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      setShowInstallButton(true);
      
      // Show install banner after a delay (only if not already installed)
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 5000); // Show after 5 seconds
      }
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setShowInstallButton(false);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
      setShowInstallBanner(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowInstallButton(false);
      setShowInstallBanner(false);
    } catch (error) {
      console.error('Error during installation:', error);
    }
  };

  const handleCloseBanner = () => {
    setShowInstallBanner(false);
  };

  if (!showInstallButton && !showInstallBanner) {
    return null;
  }

  return (
    <>
      {/* Install Button (always available when installable) */}
      {showInstallButton && (
        <Button
          variant="outlined"
          startIcon={<InstallIcon />}
          onClick={handleInstallClick}
          size="small"
          sx={{ 
            position: 'fixed', 
            top: 16, 
            right: 16, 
            zIndex: 1300,
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          Install App
        </Button>
      )}

      {/* Install Banner (shows after delay) */}
      <Snackbar
        open={showInstallBanner}
        autoHideDuration={10000}
        onClose={handleCloseBanner}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          action={
            <>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleInstallClick}
                startIcon={<InstallIcon />}
              >
                Install
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseBanner}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          Install Dining-x Talabat for quick access!
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
